/** @format */

import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	HostBinding,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild,
	input
} from '@angular/core';
import { HmfBadgeComponent, HmfButtonComponent } from '@dsg-tech/homefield-angular';
import {
	HomefieldButtonModule,
	HomefieldPriceModule,
	HomefieldProductImageComponent,
	HomefieldProductImageModule,
	HomefieldStarRatingsComponent,
	HomefieldStrikethroughPriceModule,
	HomefieldTextLinkModule
} from '@homefield';
import {
	HomefieldIconLibraryModule,
	HomefieldIconsLibraryService,
	homefieldIconAlertInfoFilled,
	homefieldIconOrderCartStandard,
	homefieldIconStarFilled,
	homefieldIconStarOutlined
} from '@dsg-tech/homefield-angular-icons';
import {
	PICKUP_MESSAGES,
	SHIPPING_MESSAGES
} from './product-availability/availability-messaging.constants';

import { CommonModule } from '@angular/common';
import { CompareBarProduct } from '@shared/data-access/interfaces';
import { CompareCheckboxComponent } from './compare-checkbox/compare-checkbox.component';
import { FavoritesLocationEnum } from '@shared/favorites/data-access/enums';
import { IAvailabilityData } from './product-availability/availability-messaging.interface';
import { ProductAvailabilityComponent } from './product-availability/product-availability.component';
import { ProductCardColorSwatchesComponent } from './product-card-color-swatches/product-card-color-swatches.component';
import { ProductCardImageSwatchesComponent } from './product-card-image-swatches/product-card-image-swatches.component';
import { ProductTitleComponent } from './product-title/product-title.component';
import { PromotionComponent } from './promotion/promotion.component';
import { UtilityService } from '@shared/utils/services';

@Component({
	selector: 'plp-components-product-card',
	standalone: true,
	imports: [
		CommonModule,
		HomefieldButtonModule,
		HomefieldIconLibraryModule,
		HomefieldPriceModule,
		HomefieldProductImageModule,
		HomefieldStarRatingsComponent,
		HomefieldStrikethroughPriceModule,
		HomefieldTextLinkModule,
		CompareCheckboxComponent,
		HmfButtonComponent,
		ProductAvailabilityComponent,
		ProductCardColorSwatchesComponent,
		ProductCardImageSwatchesComponent,
		ProductTitleComponent,
		PromotionComponent,
		HmfBadgeComponent
	],
	templateUrl: './product-card.component.html',
	styleUrls: ['./product-card.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductCardComponent implements OnInit, OnChanges {
	@ViewChild('productCardImage') productCardImage: HomefieldProductImageComponent;
	@Input() productAvailabilityData: IAvailabilityData;
	@Input() promo: string;
	@Input() selectedStoreId: string;
	@Input() chainAbbr = 'dsg';
	@Input() referrer = '';
	@Input() seoUrl: string;
	@Input() title = '';
	@Input() colors;
	@Input() ecode = '';
	@Input() sku = '';
	@Input() mapPriceIndicator: number;
	@Input() priceIndicator: number;
	@Input() prices: {
		maxlistprice: string;
		maxofferprice: string;
		minlistprice: string;
		minofferprice: string;
	};
	@Input() productQuickViewEnabled;
	@Input() attributes;
	@Input() imageUrl = '';
	@Input() addColorToProductUrl = true;
	@Input() isSponsored = false;
	@Input() isUnavailable = false;
	@Input() ratingCount: number | string = 0;
	@Input() ratingValue = 0;
	@Input() showCustomerRating = false;
	@Input() favoritesAttributes = [];
	@Input() inMauiList = false;
	@Input() isDisabled = false;
	@Input() isCustomOrPersonalizedProduct = false;
	@Input() isLCPImage = false;
	@Input() isNgOptimizedImageSupported = false;
	@Input() isComingSoon = false;
	@Input() location;
	@Input() isAntiClearance = false;
	@Input() clickTrackerUrl = '';
	@Input() availabilityOnProductCardsEnabled = true;
	@Input() categoryGroupId = '';
	@Input() categoryIdentifier = '';
	@Input() categoryType = '';
	@Input() oosListEnabled = false;
	@Input() recommendedAnalyticsData;
	@Input() compareCheckboxEnabled = false;
	@Input() selectedForCompare = false;
	imageSwatchesEnabled = input(false);
	sharedList = input<boolean>(false);
	badge = input('', { transform: this.formatBadge.bind(this) });

	/**
	 * @description Feature flag to display "Sponsored" label at the right
	 * bottom of cards for sponsored and Nike exclusive products.
	 */
	@Input() showNewSponsoredLabel = false;
	@HostBinding('attr.data-em') @Input() dataEm = '';

	@Output() openAttributesPanelClicked = new EventEmitter();
	@Output() productCardClick = new EventEmitter<{ event: PointerEvent; pdpUrl: string }>();
	@Output() ecodeSelected = new EventEmitter<string>();
	@Output() compareCheckboxClick = new EventEmitter<{
		compareData: CompareBarProduct;
		isChecked: boolean;
	}>();

	selectedSwatchColor: string;
	isNikeExclusive = false;
	updatedAssetSeoUrl = '';
	learnMoreUrl = '';
	pdpUrl = '';
	productCardAriaLabel = '';

	protected readonly FavoritesLocationEnum = FavoritesLocationEnum;

	get isOOS(): boolean {
		return (
			this.productAvailabilityData?.onlineInventoryMessage === 'OutOfStock' &&
			this.productAvailabilityData?.inStoreInventoryMessage === 'OutOfStock' &&
			this.oosListEnabled
		);
	}

	constructor(
		private readonly homefieldIconsLibraryService: HomefieldIconsLibraryService,
		private readonly utilityService: UtilityService
	) {
		this.homefieldIconsLibraryService.registerIcons([
			homefieldIconOrderCartStandard,
			homefieldIconStarOutlined,
			homefieldIconStarFilled,
			homefieldIconAlertInfoFilled
		]);
	}

	ngOnInit(): void {
		this.pdpUrl =
			this.seoUrl || `/p/${encodeURIComponent(this.title)}-${this.ecode}/${this.ecode}`;

		// Move this check out of card once pla is using state
		if (this.attributes) {
			this.isNikeExclusive = JSON.parse(this.attributes).find(
				(attribute) => attribute['5632'] && attribute['5632'] === 'Nike APE Exclusive'
			);
		}

		// Set referrer as queryParam for PLA
		if (this.referrer && this.referrer === 'pla') {
			this.pdpUrl = `${this.pdpUrl}?recid=PLA`;
		}

		// Check to see if we need to add categoryId or Clearance to the URL
		if (
			this.categoryType === 'S' ||
			(this.categoryIdentifier && this.categoryIdentifier.lastIndexOf('SKU-', 0) === 0)
		) {
			this.pdpUrl = this.addQueryParam(this.pdpUrl, 'categoryId', this.categoryGroupId);
		} else if (this.categoryIdentifier?.startsWith('Clearance')) {
			this.pdpUrl = this.addQueryParam(this.pdpUrl, 'Clearance', 'true');
		}

		if (this.colors?.length) {
			this.selectedSwatchColor = this.colors[0];
		}

		this.addParamsToUrls();
		this.productCardAriaLabel = this.buildProductCardAriaLabel();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes?.productAvailabilityData?.currentValue || changes?.promo?.currentValue) {
			this.productCardAriaLabel = this.buildProductCardAriaLabel();
		}
	}

	openQuickview(): void {
		// redirect athlete to pdp if product is customized or personalized due to quickview not supporting those products.
		if (this.isCustomOrPersonalizedProduct) {
			window.location.href = this.pdpUrl;
			return;
		}

		const shouldUseCategoryGroupId =
			this.categoryType === 'S' || this.categoryIdentifier?.startsWith('SKU-');

		let productQuickViewParams = {
			clearance: this.categoryIdentifier?.startsWith('Clearance') ?? false,
			skuCategory: shouldUseCategoryGroupId && this.categoryGroupId,
			preview: '',
			skuPartNumber: this.sku,
			defaultColor: this.selectedSwatchColor
		};

		if (this.recommendedAnalyticsData) {
			productQuickViewParams = {
				...productQuickViewParams,
				additionalData: this.recommendedAnalyticsData
			} as any;
		}

		window.quickview.openProductQuickView(this.ecode, this.referrer, productQuickViewParams);
	}

	colorSwatchChanged(newColor: string): void {
		// Only fire if the color changes, otherwise this would fire on hover everytime
		if (this.selectedSwatchColor !== newColor) {
			this.selectedSwatchColor = newColor;
			this.addParamsToUrls();
		}
	}

	openAttributesPanel($event) {
		this.openAttributesPanelClicked.emit($event);
	}

	productCardClicked(event: PointerEvent): void {
		this.productCardClick.emit({ event, pdpUrl: this.updatedAssetSeoUrl });
	}

	handleCompareSelection(isChecked: boolean): void {
		const compareData: CompareBarProduct = {
			imgURL: this.productCardImage.imageSrc,
			ecode: this.ecode,
			name: this.title,
			pdpURL: this.updatedAssetSeoUrl
		};

		this.compareCheckboxClick.emit({ compareData, isChecked });
	}

	private addParamsToUrls(): void {
		if (!this.pdpUrl) return;

		// Check to see if we need to add color swatch to URL
		if (this.addColorToProductUrl && this.selectedSwatchColor) {
			this.updatedAssetSeoUrl = this.addQueryParam(
				this.pdpUrl,
				'color',
				this.selectedSwatchColor
			);
		} else {
			this.updatedAssetSeoUrl = this.pdpUrl;
		}

		if (this.isNikeExclusive) {
			this.learnMoreUrl = this.pdpUrl;

			if (this.referrer === 'pla' && !this.learnMoreUrl.includes('?recid=PLA')) {
				this.learnMoreUrl = `${this.learnMoreUrl}?recid=PLA`;
			}

			if (this.addColorToProductUrl && this.selectedSwatchColor) {
				this.learnMoreUrl = this.addQueryParam(
					this.learnMoreUrl,
					'color',
					this.selectedSwatchColor
				);
			}
		}

		if (this.chainAbbr === 'dsg' && this.isAntiClearance === true) {
			this.updatedAssetSeoUrl = this.addQueryParam(
				this.updatedAssetSeoUrl,
				'antiClearance',
				'true'
			);
		}

		// If this is a sponsored product, we need to add the clickTrackerUrl to the seoUrl
		// but we have to do it at the end so that it includes the other parameters
		if (this.isSponsored) {
			this.updatedAssetSeoUrl = `${this.clickTrackerUrl}${this.updatedAssetSeoUrl}`;
		}
	}

	private addQueryParam(url: string, param: string, value: string): string {
		const connector = url.includes('?') ? '&' : '?';
		return `${url}${connector}${param}=${value}`;
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	trackByFn(index: number, _item: any): number {
		return index;
	}

	public ecodeSelectedEmitter(): void {
		this.ecodeSelected.emit(this.ecode);
	}

	private buildProductCardAriaLabel(): string {
		let productCardAriaLabel = `${this.title}`;

		const productCardAriaLabelDescriptions = {
			pricing: this.getPricingString() || '',
			reviewStars: this.getReviewStarsAriaLabel() || '',
			promo: this.promo || '',
			fulfillment: this.getFulfillmentAriaLabel() || ''
		};

		Object.keys(productCardAriaLabelDescriptions).forEach((key) => {
			if (productCardAriaLabelDescriptions[key]) {
				productCardAriaLabel += `, ${productCardAriaLabelDescriptions[key]}`;
			}
		});

		return productCardAriaLabel;
	}

	private getPricingString(): string {
		let newPriceString = '';

		// Full Price - no Price In Cart (PiC) nor previous or 'was' price (WAS)
		if (this.priceIndicator === 1 && this.mapPriceIndicator === 0) {
			newPriceString = `$${this.prices.minofferprice}`;

			if (this.prices.minofferprice !== this.prices.maxofferprice) {
				newPriceString += ` to $${this.prices.maxofferprice}`;
			}
		}

		// Sale Price - WAS price present, no PiC
		if (this.priceIndicator === 0 && this.mapPriceIndicator === 0) {
			let lowerPrice = `New Lower Price: $${this.prices.minofferprice}`;

			if (this.prices.minofferprice !== this.prices.maxofferprice) {
				lowerPrice += ` to $${this.prices.maxofferprice}`;
			}

			let wasPrice = `Previous Price: $${this.prices.minlistprice}`;

			if (this.prices.minlistprice !== this.prices.maxlistprice) {
				wasPrice += ` to $${this.prices.maxlistprice}`;
			}

			newPriceString = `${lowerPrice} , ${wasPrice}`;
		}

		// PiC and WAS
		if (this.mapPriceIndicator === 1) {
			newPriceString = `See Price in Cart. Previous Price: $${this.prices.minlistprice}`;

			if (this.prices.minlistprice !== this.prices.maxlistprice) {
				newPriceString += ` to $${this.prices.maxlistprice}`;
			}
		}

		return newPriceString;
	}

	private getReviewStarsAriaLabel(): string {
		if (!this.showCustomerRating || !this.ratingCount) {
			return '';
		}

		// ratingValue % 1 !== 0 tells if the number has decimals or not
		return `${this.ratingValue % 1 !== 0 ? this.ratingValue.toFixed(1) : this.ratingValue} out of 5 stars based on ${this.ratingCount} reviews`;
	}

	private getFulfillmentAriaLabel(): string {
		let fulfillmentAriaLabel = '';
		let shippingMessage = '';
		let pickupMessage = '';
		if (this.productAvailabilityData) {
			shippingMessage =
				SHIPPING_MESSAGES[this.productAvailabilityData.onlineInventoryMessage]?.text ?? '';
			pickupMessage =
				PICKUP_MESSAGES[this.productAvailabilityData.inStoreInventoryMessage]?.text ?? '';
		}

		if (shippingMessage) {
			fulfillmentAriaLabel += shippingMessage;
		}

		if (shippingMessage && pickupMessage) {
			fulfillmentAriaLabel += ', ';
		}

		if (pickupMessage) {
			fulfillmentAriaLabel += `${pickupMessage}`;
		}

		return fulfillmentAriaLabel;
	}

	private formatBadge(badgeValue: string): string {
		if (this.isNikeExclusive) {
			return 'NikeAPEExclusive';
		}

		if (
			!badgeValue ||
			(this.chainAbbr !== 'dsg' && badgeValue.toLowerCase() === 'dicksexclusive')
		) {
			return '';
		}
		return this.utilityService.removeWhiteSpace(badgeValue);
	}
}
